import { CSSProperties, useMemo } from 'react'
import { Typography, IconButton, styled } from '@mui/material'
import {
  CallMadeRounded as CallMadeIcon,
  CallReceivedRounded as CallReceivedIcon,
  CallMissedRounded as CallMissedIcon,
} from '@mui/icons-material'
import {
  MessageSender,
  CallMessageAttachment,
} from '@weblite/ts-action/message'
import { CallOutlinedIcon } from '$common/icons'
import { useClientUser } from '$logic/client-user'
import {
  callService,
  formatCallDuration,
  getCallMessageStatusText,
} from '$logic/call'
import { MessageMenuIcon } from '../../components/menu-icon'

const Container = styled('div')`
  width: 280px;
  display: flex;
  position: relative;
  align-items: center;

  padding: 8px 10px 8px 32px;
  box-sizing: border-box;
  overflow: hidden;
`

const CallButton = styled(IconButton)({
  width: 48,
  height: 48,
  position: 'relative',
})

const CallStatusBadge = styled('div')`
  width: 20px;
  height: 20px;

  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: #fff;
  border: 1px solid #0000000d;
  border-radius: 50%;

  position: absolute;
  bottom: -2px;
  left: -4px;

  & > svg {
    width: 14px;
    height: 14px;
  }
`

const TextsContainer = styled('div')`
  display: flex;
  flex-grow: 1;
  flex-direction: column;

  /* @noflip */
  margin-right: 10px;
  overflow: hidden;
`

const DurationTypography = styled(Typography)`
  /* @noflip */
  direction: ltr;
  margin-top: 4px;
`

export function Call({
  attachment,
  statusComp,
  rootStyle = undefined,
  roomId,
  sender,
  iid,
}: {
  attachment: CallMessageAttachment
  statusComp: JSX.Element | null
  rootStyle?: CSSProperties
  roomId: string
  sender: MessageSender
  iid: number
}) {
  const { id: senderId } = sender
  const { status, duration } = attachment
  const { id: clientUserId } = useClientUser()

  const fromMe = senderId === clientUserId
  const statusText = useMemo(() => getCallMessageStatusText(status, senderId), [
    senderId,
    status,
  ])

  const formattedDuration = useMemo(() => formatCallDuration(duration), [
    duration,
  ])

  const StatusIcon =
    status === 'timed-out'
      ? CallMissedIcon
      : fromMe
      ? CallMadeIcon
      : CallReceivedIcon

  return (
    <>
      <Container sx={rootStyle}>
        <CallButton
          sx={{ bgcolor: fromMe ? 'primary100' : 'primary50' }}
          onClick={() => callService.startCall(roomId, 'PV', 'call')}
        >
          <CallOutlinedIcon sx={{ color: 'primary500' }} />

          <CallStatusBadge>
            <StatusIcon
              sx={{
                color: status === 'successful' ? 'success.main' : 'error.main',
              }}
            />
          </CallStatusBadge>
        </CallButton>

        <TextsContainer>
          <Typography
            variant="messageBold"
            noWrap
            color={fromMe ? 'neutral000' : 'neutral900'}
          >
            {statusText}
          </Typography>

          {status === 'successful' && formattedDuration && (
            <DurationTypography variant="caption1" color="gray">
              {formattedDuration}
            </DurationTypography>
          )}
        </TextsContainer>

        <MessageMenuIcon iid={iid} roomId={roomId} fromMe={fromMe} />
      </Container>
      {statusComp}
    </>
  )
}
